import React, { useRef, useState, useEffect } from "react"
import { useTheme } from '~/contexts/ThemeContext'
import { usePdp } from '~/contexts/PdpContext'
import { jnstyls } from '~/utils/helpers'

import BreadCrumb from '~/components/pdp/breadcrumb'
import ImgStrip from '~/components/pdp/img-strip'
import Title from '~/components/pdp/title'
import Colorways from '~/components/pdp/colorways'
import Alts from '~/components/pdp/alts'
import Sizes from '~/components/shared/sizes'
import Hem from '~/components/pdp/hem'
import Cta from '~/components/pdp/cta'
import Bis from '~/components/pdp/bis'
import ShipPickup from '~/components/pdp/pickup'
import CxChat from '~/components/pdp/cx-chat'
// import PdpEdd from '~/components/pdp/edd'
import Outfit from '~/components/pdp/outfit'
import InfoAccord from '~/components/pdp/info-accord'
import CrossSell from '~/components/pdp/cross-sell'
import HemInfo from '~/components/pdp/hem-info'
import PdpBlocks from '~/components/pdp/pdp-blocks'
import StoresPdpPanel from '~/components/panels/stores-pdp'
import StickyCta from '~/components/pdp/sticky-cta'
import FavProd from '~/components/shared/fav-prod'
import Reviews from '~/components/pdp/reviews'

import * as styles from "~/styles/pdp/pdp.module.scss"

const Pdp = () => {
  const { 
    product, showCta, showBis, wasRedirect, showAfterp, showShipInfo, showOutfits, 
    recProds, showHemInfo, setShowHemInfo, shipCheck, showStoresPanel, reviews,
    showStickyCta, setShowStickyCta
  } = usePdp()

  const { is_gift, is_single, final_sale, media, isQuick } = product
  const { 
    pdpVariant, isMobile, customer
  } = useTheme()

  const [didLoad, setDidLoad] = useState(false)
  const [favTest, setFavTest] = useState(false)

  const show_newship = () => {
    return (!is_gift && showCta && (shipCheck?.ship || shipCheck?.pickup))
  }

  const pdpRef = useRef(null)
  const ctaRef = useRef(null)

  useEffect(() => {
    if (!didLoad) return
    const cta_el = ctaRef.current
    const cta_top = cta_el.offsetTop
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (window.scrollY < 200) return
          let past_top = window.scrollY > cta_top && !entry.isIntersecting
          let change = past_top && !showStickyCta
          if (window.scrollY < 200 || (showStickyCta && entry.isIntersecting)) change = false
          setShowStickyCta(change)
        });
      },
      {
        root: null, 
        rootMargin: '0%', 
        threshold: 0.1 
      },
    )

    const enable_sticky = !isQuick && isMobile && cta_el
    if (enable_sticky) observer.observe(cta_el)

    return () => {
      if (enable_sticky) observer.unobserve(cta_el)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [didLoad])

  useEffect(() => {
    setTimeout(()=>{
      setDidLoad(true)
    }, 205)
    if (localStorage.getItem('ward')) setFavTest(true)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  return (
    <div className={jnstyls(['pdp', styles.pdp])}>
      <h1 className="visually-hidden">
        {product.title}
      </h1>

      <BreadCrumb />

      {wasRedirect && (
        <div className={styles.wasredir}>
          <span>Sorry, that color is no longer available, but check out the available colors below.</span>
        </div>
      )}

      <div 
        ref={pdpRef}
        style={{transition: 'opacity 0.2s ease-in-out'}}
      >
        <div 
          className={styles.npdp} 
          plpima={product.pl_pima} 
          mid={product.mid} 
        >

          <div className={styles.top}>
            <Title mobiTop={true} />
          </div>

          <div className={styles.content}>
            {(media && media.length > 0) && (
              <div className={jnstyls([styles.images, styles.strip_wr])} style={{minHeight: '70vh'}}>
                <ImgStrip 
                  media={media} 
                  handle={product.handle} 
                  title={product.title} 
                />
              </div>
            )}
          </div>

          <div className={styles.side}>
            
            <div d-title="">
              <Title />
            </div>

            {(customer && favTest) && (
              <FavProd prod_id={product.mid} />
            )}

            {product?.color?.handle && (
              <Colorways />
            )}

            {product.alt_cuts && (
              <Alts />
            )}

            {is_single && product.sizing && (
              <div className={jnstyls([styles.info, styles.size_info])}>
                <div className={styles.label}></div>
                <div>
                  <button type="button" className={`aria_btn`} pl-sizing="">
                    Dimensions / Fit Guide
                  </button>
                </div>
              </div>
            )}

            <div data-product-detail-form="">
              <input type="hidden" id="bis-product" name="bis-product" value={product.id} />
              
              {(product.variants && product.variants.length > 0) && (
                <Sizes />
              )}

              {product.hem && (
                <Hem />
              )}

              {is_gift && (
                <>
                  <input type="hidden" gift-hide="name" name="properties[Recipient Name]" value="" />
                  <input type="hidden" gift-hide="email" name="properties[Recipient Email]" value="" />
                  <input type="hidden" gift-hide="date" name="properties[Delivery Date]" value="" />
                  <input type="hidden" gift-hide="message" name="properties[Message]" value="" />
                </>
              )}

              <div ref={ctaRef}>
                {showCta && (
                  <Cta npdp={true} />
                )}

                {showBis && (
                  <Bis />
                )}
              </div>

              {/*{(!noAvail && check_cta() && isLow && isLow <= 25) && (
                <div className={styles.low}>
                  {isLow <= 5 ? `${isLow} left in stock` : 'Low Stock'}
                </div>
              )}*/}

              {showAfterp ? (
                <div id={styles.afterpay}>
                  <afterpay-placement 
                    data-locale="en_US"
                    data-currency="USD" 
                    data-amount={product.first_product_price}
                    data-modal-theme="white"
                    data-logo-type="lockup"
                  >{process.env.NODE_ENV !== "production" && (<p className='afterpay-paragraph'>After pay goes here</p>)}</afterpay-placement>
                </div>
              ) : (<br/>)}

              {(!showBis && show_newship()) && (
                <ShipPickup />
              )}

              <CxChat title={product.title} />

              {showShipInfo && (
                <div className={styles.ship_info}>
                  {final_sale && (
                    <p className={styles.finalsale}>
                      Final Sale. This item is not eligible for returns, exchanges, refunds or store credit.
                    </p>
                  )}

                  {/*{(!csoon && check_cta()) && (
                    <PdpEdd price={product.first_product_price} variants={variants} />
                  )}*/}
                </div>
              )}

              <div d-info="">
                <InfoAccord product={product} />
              </div>

              {showOutfits && (
                <Outfit />
              )}
            </div>

          </div>

          <div m-info="">
            <InfoAccord product={product} />
          </div>
        </div>

        {(reviews && reviews?.length > 0) && (
          <Reviews />
        )}

        {product.product_line.blocks && (
          <PdpBlocks />
        )}
        
        {(recProds.length > 0) && (
          <CrossSell />
        )}

        {showHemInfo && (
          <HemInfo onClose={setShowHemInfo} />
        )}
      </div>

      {showStoresPanel && (
        <StoresPdpPanel variant={pdpVariant} />
      )}
      
      <StickyCta />
    </div>
  )
}

export default Pdp